import { memo, useId, useMemo, useState } from 'react';

import { Collapse } from '@material-ui/core';

import { RiArrowRightSLine } from '@remixicon/react';
import classNames from 'classnames';

import { Text, VisuallyHidden } from '@spotify-internal/encore-web';

import { DisplayItem } from '../../hooks/groupChatEvents';
import {
  AgentProgress,
  type AgentProgressItem,
  AgentProgressItems,
  toAgentProgressItems,
} from './AgentProgress';

import styles from './ThoughtProcess.module.css';

type ThoughtProcessProps = {
  items: DisplayItem[];
  isActive: boolean;
  isInterrupted: boolean;
  defaultExpanded?: boolean;
  elevated?: boolean;
  collapseConsecutiveCalls?: boolean;
};

export const formatThoughtProcessSummary = (
  items: AgentProgressItem[],
  isInterrupted: boolean,
): string => {
  const messageCount = items.filter(item => item.type === 'thinking').length;
  const toolCallCount = items.reduce(
    (count, item) =>
      item.type === 'tool-group' ? count + item.entries.length : count,
    0,
  );
  const parts: string[] = [];
  if (messageCount > 0) {
    parts.push(
      `${messageCount} ${messageCount === 1 ? 'message' : 'messages'}`,
    );
  }
  if (toolCallCount > 0) {
    parts.push(
      `${toolCallCount} ${toolCallCount === 1 ? 'tool call' : 'tool calls'}`,
    );
  }
  const summary = parts.join(', ') || 'Thought process';
  return isInterrupted ? `${summary} (interrupted)` : summary;
};

export const ThoughtProcess = memo(
  ({
    items,
    isActive,
    isInterrupted,
    defaultExpanded = false,
    elevated = false,
    collapseConsecutiveCalls = true,
  }: ThoughtProcessProps) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const panelId = useId();
    const progressItems = useMemo<AgentProgressItem[]>(
      () => toAgentProgressItems(items),
      [items],
    );

    if (isActive) {
      return (
        <AgentProgress
          items={progressItems}
          isInterrupted={isInterrupted}
          collapseConsecutiveCalls={collapseConsecutiveCalls}
        />
      );
    }

    const summary = formatThoughtProcessSummary(progressItems, isInterrupted);
    const summaryNeedsContext = !summary.startsWith('Thought process');

    return (
      <div>
        <button
          type="button"
          className={styles.header}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => {
            setExpanded(prev => !prev);
          }}
        >
          {summaryNeedsContext && (
            <>
              <VisuallyHidden>Thought process:</VisuallyHidden>{' '}
            </>
          )}
          <Text semanticColor="textSubdued">{summary}</Text>
          <RiArrowRightSLine
            size={16}
            className={classNames(styles.arrow, {
              [styles.arrowExpanded]: expanded,
            })}
          />
        </button>
        <Collapse in={expanded}>
          <div id={panelId}>
            <div
              className={classNames(styles.content, {
                [styles.elevatedContent]: elevated,
              })}
            >
              <AgentProgressItems
                items={progressItems}
                shimmerLatestThinking={false}
                collapseConsecutiveCalls={collapseConsecutiveCalls}
              />
            </div>
          </div>
        </Collapse>
      </div>
    );
  },
);
ThoughtProcess.displayName = 'ThoughtProcess';
